<template>
  <modal
    :show="show"
    modal-classes="modal-secondary"
    size="lg"
    @close="$emit('close')"
  >
    <template slot="header">
      <h5 class="modal-title">
        <i class="fas fa-power-off text-warning mr-2"></i>
        {{ $t("ESTABLISHMENTS.DEACTIVATE_ESTABLISHMENT_TITLE") }}
      </h5>
    </template>

    <div>
      <p>
        {{ $t("ESTABLISHMENTS.DEACTIVATE_ESTABLISHMENT_INTRO") }}
        <strong v-if="establishment">{{ establishment.company_name }}</strong>
      </p>

      <base-alert type="warning" class="mt-3 mb-3">
        <span class="alert-text">
          {{ $t("ESTABLISHMENTS.DEACTIVATE_RECAP_HEADER") }}
        </span>
      </base-alert>

      <ul class="deactivate-recap">
        <li><i class="fas fa-ban text-warning mr-2"></i>{{ $t("ESTABLISHMENTS.DEACTIVATE_RECAP_ORDERS") }}</li>
        <li><i class="fas fa-ban text-warning mr-2"></i>{{ $t("ESTABLISHMENTS.DEACTIVATE_RECAP_SUBMISSION") }}</li>
        <li><i class="fas fa-ban text-warning mr-2"></i>{{ $t("ESTABLISHMENTS.DEACTIVATE_RECAP_TRANSFERS") }}</li>
        <li><i class="fas fa-ban text-warning mr-2"></i>{{ $t("ESTABLISHMENTS.DEACTIVATE_RECAP_INTERNAL_INVOICES") }}</li>
        <li><i class="fas fa-ban text-warning mr-2"></i>{{ $t("ESTABLISHMENTS.DEACTIVATE_RECAP_WEEKLY_REPORTS") }}</li>
        <li><i class="fas fa-ban text-warning mr-2"></i>{{ $t("ESTABLISHMENTS.DEACTIVATE_RECAP_CASH_REGISTERS") }}</li>
        <li><i class="fas fa-ban text-warning mr-2"></i>{{ $t("ESTABLISHMENTS.DEACTIVATE_RECAP_ALL_LINKED") }}</li>
      </ul>

      <h6 class="mt-4 mb-2 text-uppercase text-muted">
        {{ $t("ESTABLISHMENTS.PENDING_ITEMS_TITLE") }}
      </h6>

      <div v-if="pendingLoading" class="text-muted py-2">
        <i class="fas fa-spinner fa-spin mr-2"></i>
        {{ $t("ESTABLISHMENTS.PENDING_LOADING") }}
      </div>

      <template v-else>
        <base-alert v-if="hasPending" type="danger" class="mb-3">
          <span class="alert-text">
            {{ $t("ESTABLISHMENTS.PENDING_WARNING") }}
          </span>
        </base-alert>

        <ul v-if="hasPending" class="deactivate-recap">
          <li
            v-for="item in pendingItems"
            :key="item.key"
            :class="{ 'pending-clickable': !!item.routeName }"
            @click="navigate(item)"
          >
            <i class="fas fa-clock text-danger mr-2"></i>
            {{ $t(item.labelKey) }}
            <span class="badge badge-danger ml-2">{{ item.count }}</span>
            <i
              v-if="item.routeName"
              class="fas fa-arrow-right text-muted ml-2 pending-go"
            ></i>
          </li>
        </ul>

        <p v-else class="text-success mb-0">
          <i class="fas fa-check-circle mr-2"></i>
          {{ $t("ESTABLISHMENTS.PENDING_NONE") }}
        </p>
      </template>

      <p class="text-muted mt-3 mb-0">
        <small>{{ $t("ESTABLISHMENTS.DEACTIVATE_RECAP_FOOTER") }}</small>
      </p>
    </div>

    <template slot="footer">
      <base-button
        type="warning"
        :disabled="loading"
        @click="$emit('confirm')"
      >
        <i class="fas fa-power-off mr-1"></i>
        {{ $t("ESTABLISHMENTS.CONFIRM_DEACTIVATION") }}
      </base-button>
      <base-button
        type="link"
        class="ml-auto"
        :disabled="loading"
        @click="$emit('close')"
      >
        {{ $t("COMMON.CANCEL") }}
      </base-button>
    </template>
  </modal>
</template>

<script>
import BaseAlert from "@/components/BaseAlert.vue";

export default {
  name: "establishment-deactivate-modal",

  components: {
    BaseAlert,
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    establishment: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pendingSummary: {
      type: Object,
      default: null,
    },
    pendingLoading: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    pendingItems() {
      if (!this.pendingSummary) {
        return [];
      }
      // Seuls les documents non-brouillon (gelés par la désactivation) sont listés.
      // Les brouillons ne sont pas affichés : ils sont supprimés à la désactivation.
      const mapping = [
        {
          key: "sales_orders",
          labelKey: "ESTABLISHMENTS.PENDING_SALES_ORDERS",
          routeName: "List SalesOrders",
        },
        {
          key: "purchases_orders",
          labelKey: "ESTABLISHMENTS.PENDING_PURCHASES_ORDERS",
          routeName: "List PurchasesOrders",
        },
        {
          key: "sales_invoices",
          labelKey: "ESTABLISHMENTS.PENDING_SALES_INVOICES",
          routeName: "List SalesInvoices",
        },
        {
          key: "purchases_invoices",
          labelKey: "ESTABLISHMENTS.PENDING_PURCHASES_INVOICES",
          routeName: "List PurchasesInvoices",
        },
        {
          key: "transfers",
          labelKey: "ESTABLISHMENTS.PENDING_TRANSFERS",
          routeName: "List EstablishmentTransfers",
        },
        {
          key: "internal_invoices",
          labelKey: "ESTABLISHMENTS.PENDING_INTERNAL_INVOICES",
          routeName: "List EstablishmentInternalInvoices",
        },
        {
          key: "special_order_reservations",
          labelKey: "ESTABLISHMENTS.PENDING_SPECIAL_ORDER_RESERVATIONS",
        },
      ];
      return mapping
        .map((item) => ({ ...item, count: this.pendingSummary[item.key] || 0 }))
        .filter((item) => item.count > 0);
    },

    hasPending() {
      return this.pendingItems.length > 0;
    },
  },

  methods: {
    navigate(item) {
      if (!item.routeName || !this.establishment) {
        return;
      }
      const query =
        item.filterType === "warehouse"
          ? {
              warehouse:
                this.establishment.warehouse &&
                this.establishment.warehouse.id,
            }
          : { establishment: this.establishment.id };
      this.$router.push({ name: item.routeName, query }).catch(() => {});
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.deactivate-recap {
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
}
.deactivate-recap li {
  padding: 0.35rem 0;
  border-bottom: 1px solid #f0f0f0;
}
.deactivate-recap li:last-child {
  border-bottom: none;
}
.deactivate-recap li.pending-clickable {
  cursor: pointer;
  border-radius: 0.25rem;
  transition: background-color 0.15s ease;
}
.deactivate-recap li.pending-clickable:hover {
  background-color: #f6f9fc;
}
.deactivate-recap li.pending-clickable .pending-go {
  opacity: 0;
  transition: opacity 0.15s ease;
}
.deactivate-recap li.pending-clickable:hover .pending-go {
  opacity: 1;
}
</style>
